import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Home,
  GraduationCap,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Settings,
  Database,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModeToggle = () => {
  const { user, logout } = useAuth();
  const [modeInfo, setModeInfo] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [showSwitchDialog, setShowSwitchDialog] = useState(false);
  const [targetMode, setTargetMode] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchModeInfo();
    // Refresh mode info every 30 seconds
    const interval = setInterval(fetchModeInfo, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchModeInfo = async () => {
    try {
      const response = await axios.get(`${API}/mode/info`);
      setModeInfo(response.data);
    } catch (error) {
      console.error('Error fetching mode info:', error);
    }
  };

  const handleSwitchMode = async () => {
    if (!targetMode) return;

    setSwitching(true);
    try {
      const response = await axios.post(`${API}/mode/switch`, {
        mode: targetMode
      });

      toast.success(`Successfully switched to ${targetMode} mode`);
      
      // Refresh mode info
      await fetchModeInfo();
      
      // Refresh the page to ensure all components use the new database
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch mode');
    } finally {
      setSwitching(false);
      setShowSwitchDialog(false);
      setTargetMode('');
    }
  };

  const handleResetTrainingData = async () => {
    setResetting(true);
    try {
      const response = await axios.post(`${API}/mode/reset-training`);
      
      toast.success('Training data reset successfully');
      
      // Refresh mode info
      await fetchModeInfo();
      
      // Refresh the page to show new data
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset training data');
    } finally {
      setResetting(false);
      setShowResetDialog(false);
    }
  };

  const confirmModeSwitch = (newMode) => {
    setTargetMode(newMode);
    setShowSwitchDialog(true);
  };

  if (!modeInfo || !user) return null;

  const currentModeConfig = modeInfo.mode_descriptions[modeInfo.current_mode];
  const isProduction = modeInfo.current_mode === 'production';
  const isTraining = modeInfo.current_mode === 'training';
  const isAdmin = user.role === 'admin';

  return (
    <>
      {/* Mode Indicator Badge */}
      <div className="flex items-center space-x-3">
        <Badge 
          className={`px-3 py-1 flex items-center space-x-2 ${
            isProduction 
              ? 'bg-green-100 text-green-800 border-green-200' 
              : 'bg-blue-100 text-blue-800 border-blue-200'
          }`}
        >
          <span className="text-lg">{currentModeConfig.icon}</span>
          <span className="font-medium">{currentModeConfig.name}</span>
        </Badge>

        {/* Admin Mode Controls */}
        {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">System Mode</p>
                <p className="text-xs text-gray-500">
                  Currently: {currentModeConfig.name}
                </p>
              </div>
              <DropdownMenuSeparator />
              
              {isTraining && (
                <DropdownMenuItem
                  onClick={() => confirmModeSwitch('production')}
                  className="flex items-center space-x-2"
                >
                  <Home className="h-4 w-4 text-green-600" />
                  <div>
                    <div className="font-medium">Switch to Production</div>
                    <div className="text-xs text-gray-500">Live shelter operations</div>
                  </div>
                </DropdownMenuItem>
              )}
              
              {isProduction && (
                <DropdownMenuItem
                  onClick={() => confirmModeSwitch('training')}
                  className="flex items-center space-x-2"
                >
                  <GraduationCap className="h-4 w-4 text-blue-600" />
                  <div>
                    <div className="font-medium">Switch to Training</div>
                    <div className="text-xs text-gray-500">Safe demo environment</div>
                  </div>
                </DropdownMenuItem>
              )}
              
              {isTraining && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setShowResetDialog(true)}
                    className="flex items-center space-x-2"
                  >
                    <RefreshCw className="h-4 w-4 text-orange-600" />
                    <div>
                      <div className="font-medium">Reset Training Data</div>
                      <div className="text-xs text-gray-500">Load fresh demo data</div>
                    </div>
                  </DropdownMenuItem>
                </>
              )}
              
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5">
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <Database className="h-3 w-3" />
                  <span>DB: {modeInfo.database_name}</span>
                </div>
                {modeInfo.last_switched && (
                  <div className="text-xs text-gray-500 mt-1">
                    Last switched: {new Date(modeInfo.last_switched).toLocaleString()}
                  </div>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Mode Switch Confirmation Dialog */}
      <Dialog open={showSwitchDialog} onOpenChange={setShowSwitchDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <span>Confirm Mode Switch</span>
            </DialogTitle>
            <DialogDescription>
              You are about to switch from {currentModeConfig.name} to{' '}
              {modeInfo.mode_descriptions[targetMode]?.name}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Current Mode</h4>
                  <div className="flex items-center space-x-2">
                    <span>{currentModeConfig.icon}</span>
                    <span className="font-medium">{currentModeConfig.name}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {currentModeConfig.description}
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Switching To</h4>
                  <div className="flex items-center space-x-2">
                    <span>{modeInfo.mode_descriptions[targetMode]?.icon}</span>
                    <span className="font-medium">{modeInfo.mode_descriptions[targetMode]?.name}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {modeInfo.mode_descriptions[targetMode]?.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Important Notes:</p>
                  <ul className="text-sm text-amber-700 mt-1 space-y-1">
                    <li>• All users will be automatically logged out</li>
                    <li>• The system will restart with the new database</li>
                    <li>• This change affects all staff members</li>
                    {targetMode === 'training' && (
                      <li>• Demo data will be loaded automatically</li>
                    )}
                    {targetMode === 'production' && (
                      <li>• Real shelter data will be accessible</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowSwitchDialog(false)}
              variant="outline"
              disabled={switching}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSwitchMode}
              disabled={switching}
              className={
                targetMode === 'production' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }
            >
              {switching ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Switching...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Switch
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Training Data Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <RefreshCw className="h-5 w-5 text-orange-600" />
              <span>Reset Training Data</span>
            </DialogTitle>
            <DialogDescription>
              This will replace all training data with fresh demo content.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">What will be reset:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• 30 demo residents (including 6 banned residents)</li>
                <li>• 5 sample staff members</li>
                <li>• 15 demo rooms with assignments</li>
                <li>• Sample case notes and documentation</li>
                <li>• Fresh time tracking data</li>
              </ul>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Warning:</p>
                  <p className="text-sm text-amber-700 mt-1">
                    This will permanently delete all existing training data and cannot be undone.
                    Only use this to get fresh demo data for new training sessions.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowResetDialog(false)}
              variant="outline"
              disabled={resetting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetTrainingData}
              disabled={resetting}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {resetting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset Training Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModeToggle;